import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EncouragementChibiGeneratorProps {
  photoFile?: File | null;
  existingPhotoUrl?: string | null;
  recipientName: string;
  onChibiGenerated: (url: string) => void;
  chibiUrl?: string | null;
}

export function EncouragementChibiGenerator({
  photoFile,
  existingPhotoUrl,
  recipientName,
  onChibiGenerated,
  chibiUrl,
}: EncouragementChibiGeneratorProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const hasPhoto = !!photoFile || !!existingPhotoUrl;

  const handleGenerate = async () => {
    if (!hasPhoto) {
      toast.info('Upload a photo first to create mini chibis!');
      return;
    }

    setIsGenerating(true);
    try {
      await (supabase.auth as any).refreshSession();

      let publicUrl = existingPhotoUrl;

      // If we have a local file, upload it to storage first
      if (photoFile && !existingPhotoUrl) {
        const { data: { user } } = await (supabase.auth as any).getUser();
        if (!user) {
          toast.error('Please sign in to generate chibis.');
          return;
        }

        const ext = photoFile.name.split('.').pop() || 'jpg';
        const path = `${user.id}/chibi-source/${Date.now()}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from('announcement-photos')
          .upload(path, photoFile, { contentType: photoFile.type, upsert: true });

        if (uploadError) {
          console.error('Photo upload error:', uploadError);
          toast.error('Could not upload photo. Please try again.');
          return;
        }

        const { data: urlData } = supabase.storage
          .from('announcement-photos')
          .getPublicUrl(path);

        publicUrl = urlData.publicUrl;
      }

      if (!publicUrl) {
        toast.error('No photo available. Please upload one first.');
        return;
      }

      const { data, error } = await supabase.functions.invoke('generate-chibi', {
        body: { photoUrl: publicUrl, recipientName },
      });

      if (error) {
        console.error('Chibi generation error:', error);
        toast.error('Could not generate chibis. Please try again.');
        return;
      }

      if (data?.chibiImageUrl) {
        onChibiGenerated(data.chibiImageUrl);
        toast.success('Mini chibis created! ✨');
      }
    } catch (err) {
      console.error('Chibi generation failed:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (!hasPhoto) return null;

  return (
    <div className="space-y-3">
      <AnimatePresence mode="wait">
        {chibiUrl ? (
          <motion.div
            key="chibi-preview"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative rounded-xl overflow-hidden border-2 border-emerald-200 shadow-lg"
          >
            <img
              src={chibiUrl}
              alt="Mini chibi characters"
              className="w-full h-auto rounded-xl"
            />
            <div className="absolute bottom-2 right-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleGenerate}
                disabled={isGenerating}
                className="bg-white/90 backdrop-blur-sm text-xs gap-1"
              >
                {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                Regenerate
              </Button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="chibi-cta"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="w-full gap-2 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-white shadow-md"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating your mini chibis...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate Mini Chibis from Photo
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-1">
              AI creates cute 3D mini characters matching the photo
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
